/**
 * Deterministic, stable color per service time slot ("HH:mm").
 * Used by floor plan V2 to visually segment double-service tables and
 * distinguish lunch vs dinner at a glance.
 *
 * Output is plain HSL — no theme tokens — because these colors need to
 * sit on top of every status background (free / reserved / arrived…)
 * and keep their identity in light + dark.
 */

const GOLDEN_ANGLE = 137.508;

const hashSlot = (slot: string): number => {
  // FNV-1a on "HH:mm" — small but stable.
  let h = 0x811c9dc5;
  for (let i = 0; i < slot.length; i++) {
    h ^= slot.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export const getSlotHue = (slot: string): number => {
  // Skew via the golden angle so neighbouring slots end up well apart.
  const n = hashSlot(slot);
  return (n % 360 + (n % 7) * GOLDEN_ANGLE) % 360;
};

export const getSlotColor = (slot: string, opts?: { saturation?: number; lightness?: number; alpha?: number }) => {
  const s = opts?.saturation ?? 72;
  const l = opts?.lightness ?? 52;
  const a = opts?.alpha ?? 1;
  const hue = getSlotHue(slot);
  return a >= 1
    ? `hsl(${hue.toFixed(1)}, ${s}%, ${l}%)`
    : `hsl(${hue.toFixed(1)} ${s}% ${l}% / ${a})`;
};
